"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const csvtojson_1 = __importDefault(require("csvtojson"));
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const helpers_1 = require("./helpers");
const typeguards_1 = require("./typeguards");
const getFieldName = (modification) => {
    switch (modification.type) {
        case 'delete_field':
            return modification.field;
        case 'add_field':
            return modification.fieldConfig.field;
        case 'rename_field':
            return modification.newName;
        default:
            throw new Error((0, common_1.finleyAssertUnreachable)(modification));
    }
};
const getFormulaStrings = (transformation) => {
    return transformation.fieldModifications
        .filter((mod) => mod.type === 'add_field')
        .map((mod) => mod.formula);
};
const checkErrors = (transformation) => {
    const errors = [];
    (0, lodash_1.default)(transformation.fieldModifications)
        .countBy(getFieldName)
        .forEach((count, fieldName) => {
        if (count > 1) {
            errors.push({
                type: 'metadataValidation',
                message: `field ${fieldName} declared multiple times`,
            });
        }
    });
    const fieldsThatMustExist = transformation.fieldModifications
        .map((mod) => {
        switch (mod.type) {
            case 'delete_field':
                return mod.field;
            case 'rename_field':
                return mod.oldName;
            default:
                return null;
        }
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
    fieldsThatMustExist.forEach((deletedField) => {
        if (!transformation.srcDataTape.baseFields.find(({ field }) => field === deletedField)) {
            errors.push({
                type: 'metadataValidation',
                message: `edit operation: field:[${deletedField}] does not exist in the input`,
            });
        }
    });
    const fieldsThatMustNotExist = transformation.fieldModifications
        .map((mod) => (mod.type === 'rename_field' ? mod.newName : null))
        .filter(common_1.typeguards.isNotNullOrUndefined);
    fieldsThatMustNotExist.forEach((mustExistField) => {
        if (transformation.srcDataTape.baseFields.find(({ field }) => field === mustExistField)) {
            errors.push({
                type: 'metadataValidation',
                message: `edit operation: field:[${mustExistField}] already exist in the input`,
            });
        }
    });
    const availableVariables = new Set(transformation.srcDataTape.baseFields.map(({ field }) => field));
    getFormulaStrings(transformation).forEach((formulaString) => {
        const formulaErrors = (0, formula_engine_1.getFormulaErrors)(formulaString, availableVariables);
        formulaErrors.errorMessages.forEach((message) => {
            errors.push({ type: 'formulaValidation', message });
        });
    });
    if (errors.length) {
        throw errors;
    }
};
const getOutputDataTape = (transformation) => {
    checkErrors(transformation);
    // we start with src tape, and apply transformation to generate new
    // tape definition and collect any errors on the way.
    const outputFields = lodash_1.default.cloneDeep(transformation.srcDataTape.baseFields);
    const getExistingFieldIndex = (fieldToCheck) => outputFields.findIndex((existing) => existing.field === fieldToCheck);
    // apply each modification
    transformation.fieldModifications.forEach((mod) => {
        switch (mod.type) {
            case 'add_field': {
                const newFieldConfig = (0, helpers_1.getSanitizedConfig)({
                    configBeforeSanitize: mod.fieldConfig,
                    // we do not want to field names to be standardized.
                    // they are already sanitized (or not) by sanitize operation as appropriate.
                    sanitizeFieldNames: false,
                });
                // add_field may replace existing definitions.
                const index = getExistingFieldIndex(newFieldConfig.field);
                if (index === -1) {
                    outputFields.push(newFieldConfig);
                }
                else {
                    outputFields[index] = newFieldConfig;
                }
                break;
            }
            case 'delete_field': {
                const fieldToBeDeleted = mod.field;
                const index = getExistingFieldIndex(fieldToBeDeleted);
                (0, common_1.finleyAssert)(index !== -1, 'field to be deleted must exist');
                outputFields.splice(index, 1);
                break;
            }
            case 'rename_field': {
                const fieldToBeDeleted = mod.oldName;
                const fieldToBeAdded = mod.newName;
                const oldFieldIndex = getExistingFieldIndex(fieldToBeDeleted);
                (0, common_1.finleyAssert)(oldFieldIndex !== -1, 'field to be renamed must exist');
                outputFields[oldFieldIndex].field = fieldToBeAdded;
                break;
            }
            default:
                (0, common_1.finleyAssertUnreachable)(mod);
        }
    });
    return {
        delimiter: ',',
        baseFields: outputFields,
        sanitizedFieldNames: false,
    };
};
const processInput = ({ inputData, transformation, formulaEngine }) => {
    const errors = [];
    const outputData = [];
    inputData.forEach((row, idx) => {
        formulaEngine === null || formulaEngine === void 0 ? void 0 : formulaEngine.setVariables(row);
        const newRow = lodash_1.default.cloneDeep(row);
        transformation.fieldModifications.forEach((modification) => {
            switch (modification.type) {
                case 'delete_field':
                    delete newRow[modification.field];
                    break;
                case 'add_field': {
                    try {
                        (0, common_1.finleyAssert)(formulaEngine, 'formula engine was not passed');
                        const formulaResult = formulaEngine.evaluate(modification.formula);
                        const typeCheckedResult = lodash_1.default.toString((0, helpers_1.getSanitizedDataTapeField)(modification.fieldConfig, lodash_1.default.toString(formulaResult))); // throws InvalidField if the result doesn't match the declared type
                        newRow[modification.fieldConfig.field] = typeCheckedResult;
                    }
                    catch (err) {
                        const message = (0, typeguards_1.isInvalidField)(err)
                            ? `Expected type ${err.expectedType} but formula returned [${err.value}]`
                            : `Runtime formula error: [${err}]`;
                        errors.push({
                            type: 'formulaValidation',
                            message,
                            field: modification.fieldConfig.field,
                            row: idx,
                        });
                    }
                    break;
                }
                case 'rename_field':
                    newRow[modification.newName] = newRow[modification.oldName];
                    delete newRow[modification.oldName];
                    break;
                default:
                    (0, common_1.finleyAssertUnreachable)(modification);
            }
        });
        outputData.push(newRow);
    });
    return { errors, outputData };
};
const doTransform = (input) => __awaiter(void 0, void 0, void 0, function* () {
    let outputDataTape;
    try {
        outputDataTape = getOutputDataTape(input.transformation);
    }
    catch (err) {
        (0, common_1.finleyAssert)(lodash_1.default.isArray(err), err);
        return { isValid: false, errors: err };
    }
    const srcDataTape = input.transformation.srcDataTape;
    const csvString = common_1.finleyBuffer.toString(input.srcDataTapeBuffer);
    const delimiter = srcDataTape.delimiter;
    const headers = yield (0, helpers_1.getHeaders)({
        csvString,
        delimiter,
        sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
    });
    const inputData = yield (0, csvtojson_1.default)({ headers, delimiter }).fromString(csvString);
    const formulaStrings = getFormulaStrings(input.transformation);
    const formulaEngine = formulaStrings.length
        ? (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulaStrings, undefined)
        : undefined;
    const { errors, outputData } = processInput({
        inputData,
        transformation: input.transformation,
        formulaEngine,
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    const outputBuffer = (0, helpers_1.rowDataToCsv)(outputData, outputDataTape);
    return { isValid: true, data: { outputBuffer }, supplementals: [] };
});
exports.default = {
    doTransform,
    getOutputDataTape,
};
//# sourceMappingURL=edit-transform.js.map